import clsx from 'clsx'
import React from 'react'

type TableRowProps = {
  children: React.ReactNode;
} & (
  {
    /**
     * Provide accessible text to describe the row for screen readers. When
     * the row has an `onClick` handler this prop is required to ensure that
     * users of screen readers understand what will happen when they select
     * the row.
     */
    accessibleLabel: string;
    onClick: (event: React.MouseEvent<HTMLTableRowElement> | React.KeyboardEvent<HTMLTableRowElement>) => void;
  } | {
    accessibleLabel?: string;
    onClick?: undefined;
  }
) & Omit<React.HTMLAttributes<HTMLTableRowElement>, 'className'>

/**
 * A CSS selector for elements that are expected to be clickable. This is not
 * guaranteed to cover every element that is _actually_ clickable because CSS
 * alone cannot detect whether an element has been made clickable via
 * JavaScript. But this should cover the most common cases as well as custom
 * components that are using ARIA attributes to indicate their interactive
 * role to screen readers.
 */
const INTERACTIVE_ELEMENTS = 'button, a, label, input, select, textarea, [contenteditable], [href], [role="button"], [role="menu"], [role="menuitem"]'

export function TableRow ({ children, accessibleLabel, onClick, ...props }: TableRowProps) {
  const isClickable = !!onClick

  function handleClick (event: React.MouseEvent<HTMLTableRowElement> | React.KeyboardEvent<HTMLTableRowElement>) {
    // Don't call onClick handler if the event target is, or is within,
    // an interactive element
    if ((event.target as HTMLElement).closest(INTERACTIVE_ELEMENTS)) {
      return false
    }

    // Don't call onClick handler if the user was selecting text
    if (window.getSelection()?.toString()) {
      return false
    }

    if (onClick) {
      onClick(event)
      return true
    }
  }

  function handleKeyDown (event: React.KeyboardEvent<HTMLTableRowElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      const handled = handleClick(event)

      if (handled) event.preventDefault()
    }
  }

  return (
    <tr
      {...props}
      aria-label={accessibleLabel}
      className={clsx(
        'group/tr rounded-lg outline-none transition',
        {
          'cursor-pointer': isClickable,
        }
      )}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {children}
    </tr>
  )
}

TableRow.displayName = 'Table.Row'
